import React, { FC, useMemo, useEffect } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { frameShapeToName } from 'functions/frameShapeToName';

interface ShapeSwitcherProps {
  frameShapes: string[];
}

const CategoryShapeSwitcher: FC<ShapeSwitcherProps> = ({ frameShapes }) => {
  const router = useRouter();
  let { category, shape, colour } = router.query;
  if (shape === 'classic') {
    shape = 'rectangle';
  }

  const getBasePath = () => {
    const path = router.pathname.split('/')[1]; // Get the first part of the path (shop or eyewear-selection)
    return category ? `/${path}/${category}` : `/${path}/frames`;
  };

  useEffect(() => {
    if (router && router.query.shape === 'classic' && !router.query.colour) {
      router.replace(`${getBasePath()}/shape/rectangle`, undefined, {
        shallow: true,
      });
    }

    if (router && router.query.shape === 'classic' && router.query.colour) {
      router.replace(`${getBasePath()}/shape/rectangle/colour/${colour}`, undefined, {
        shallow: true,
      });
    }
  }, [router]);

  const handleShapeChange = (selectedShape: string) => {
    const normalizedShape = shape === 'classic' ? 'rectangle' : shape;
    const normalizedSelectedShape = selectedShape.toLowerCase() === 'classic' ? 'rectangle' : selectedShape.toLowerCase();

    const basePath = getBasePath();
    let newPath = '';
    let query: { [key: string]: string | string[] } = {};

    if (normalizedShape === normalizedSelectedShape) {
      if (Array.isArray(colour) && colour.length > 0) {
        newPath = `${basePath}`;
        query = { colour };
      } else if (typeof colour === 'string') {
        newPath = `${basePath}/colour/${colour}`;
      } else {
        newPath = basePath;
      }
    } else {
      const urlShape = selectedShape.toLowerCase() === 'classic' ? 'rectangle' : selectedShape.toLowerCase();
      if (Array.isArray(colour) && colour.length > 0) {
        newPath = `${basePath}/shape/${urlShape}`;
        query = { colour };
      } else if (typeof colour === 'string') {
        newPath = `${basePath}/shape/${urlShape}/colour/${colour}`;
      } else {
        newPath = `${basePath}/shape/${urlShape}`;
      }
    }

    router.push({ pathname: newPath, query });
  };

  const shapeButtons = useMemo(() => {
    return frameShapes.map((frameShape) => (
      <ShapeButton
        key={frameShape}
        shape={frameShape}
        imagePath={`https://w.dresden.vision/images/icons/shape_${frameShape.toLowerCase()}.png`}
        isSelected={
          shape === frameShape.toLowerCase() ||
          (shape === 'rectangle' && frameShape.toLowerCase() === 'classic') ||
          (shape === 'classic' && frameShape.toLowerCase() === 'rectangle')
        }
        onClick={() => handleShapeChange(frameShape)}
      />
    ));
  }, [frameShapes, shape, colour]);

  useEffect(() => {
    if (shape === 'rectangle') {
      shape = 'classic';
    }
    // Ensure the correct shape is selected when the page loads
    if (shape && !frameShapes.map(s => s.toLowerCase()).includes(shape.toString().toLowerCase())) {
      const basePath = getBasePath();
      if (Array.isArray(colour) && colour.length > 0) {
        router.replace({ pathname: basePath, query: { colour } });
      } else if (typeof colour === 'string') {
        router.replace(`${basePath}/colour/${colour}`);
      } else {
        router.replace(basePath);
      }
    }
  }, [shape, category, colour, frameShapes, router]);

  return (
    <div className="w-full flex flex-col md:flex-row mt-4 md:mt-0">
      <span className="text-sm font-medium text-[#616161] hidden md:block mr-2 max-w-[90px] mt-[3px]">
        Filter by Frame Shape
      </span>
      <div className="flex flex-row gap-2 md:gap-x-4 ">
        {shapeButtons}
      </div>
    </div>
  );
};

interface ShapeButtonProps {
  shape: string;
  imagePath: string;
  isSelected: boolean;
  onClick: () => void;
}

const ShapeButton: FC<ShapeButtonProps> = ({ shape, imagePath, isSelected, onClick }) => {
  const frameShapeToNameFilter = (shape: string) => {
    if (shape === 'classic') return 'Rectangle';
    if (shape === 'round') return 'Round ';
    return '';
  }

  return (
    <button
      className={`flex-1 md:min-w-[190px] min-w-[160px] h-auto md:h-[50px] border rounded-md flex flex-col items-center justify-center p-2 ${isSelected ? 'border-black' : 'border-[#CACACA] hover:border-[#AEADAD]'
        }`}
      onClick={onClick}
    >
      <div className='flex flex-row items-center justify-center gap-2'>
        <div className="flex items-center justify-center md:block hidden">
          <p className={`text-[14px] font-medium ${isSelected ? 'text-black' : 'text-[#959595]'
            }`}>
            {frameShapeToNameFilter(shape)}
          </p>
        </div>
        <div className="relative w-[80px] h-[26px]">
          <Image
            src={imagePath}
            alt={`${shape} shape`}
            fill
            className="object-contain"
          />
        </div>
      </div>
      <span className="mt-2 text-center text-sm text-gray-700 md:hidden">
        {frameShapeToNameFilter(shape)}
      </span>
    </button>
  );
};

export default CategoryShapeSwitcher;
